/**
 * A horizontally scrolling row that admits it scrolls.
 *
 * The documents page has nine category filters. On a laptop they overflow, and
 * `overflow-x-auto` clipped the ninth cleanly at the container edge — which
 * reads as "that is all of them", not "there is more". The content was reachable
 * the whole time; nothing said so. With a mouse, horizontal scroll is
 * discoverable only by accident.
 *
 * So a fade appears on whichever side has content beyond it, and disappears at
 * each end. It is drawn with a gradient from the page background rather than a
 * shadow, so a filter sliding under it looks like it continues past the edge
 * rather than being covered by a bar.
 *
 * The fades are `aria-hidden` and `pointer-events-none`: they are a hint about
 * the scroll, not content, and a keyboard or screen-reader user reaches every
 * child through normal tab order regardless.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@pazportal/ui';
import { edgeState, type ScrollEdges } from '../lib/scrollEdges';

export function ScrollRow({
  children,
  className,
  ...rest
}: {
  children: React.ReactNode;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState<ScrollEdges>({ left: false, right: false });

  const measure = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    // The arithmetic, including its sub-pixel tolerance, lives in lib/ where it
    // can be tested; this only reads the DOM.
    setEdges(edgeState(node.scrollLeft, node.scrollWidth, node.clientWidth));
  }, []);

  useEffect(() => {
    measure();
    const node = ref.current;
    if (!node) return;
    // Overflow changes with the viewport and with the children, and neither
    // fires a scroll event — so observe both rather than measuring once.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    for (const child of Array.from(node.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [measure, children]);

  return (
    <div className="relative">
      <div
        ref={ref}
        onScroll={measure}
        className={cn('overflow-x-auto', className)}
        {...rest}
      >
        {children}
      </div>
      {edges.left && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-bg to-transparent"
        />
      )}
      {edges.right && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-bg to-transparent"
        />
      )}
    </div>
  );
}
